import { useState, useEffect } from "react";
import Layout from "../components/Layout";
import api from "../lib/api";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Checkbox } from "../components/ui/checkbox";
import { ShieldCheck, Plus, Pencil, Trash, Lock } from "@phosphor-icons/react";
import { toast } from "sonner";

const PERMISSION_LABELS = {
  view_dashboard: "View Dashboard",
  manage_programmes: "Manage Programmes",
  manage_courses: "Manage Courses",
  manage_users: "Manage Users",
  create_users: "Create Users",
  manage_payments: "Manage Payments",
  view_analytics: "View Analytics",
  view_revenue: "View Revenue Data",
  manage_meetings: "Manage Meetings",
  manage_certificates: "Manage Certificates",
  manage_attendance: "Manage Attendance",
  manage_gallery: "Manage Gallery",
  manage_careers: "Manage Careers",
  manage_forum: "Manage Forum",
  manage_messages: "Manage Messages",
  manage_quizzes: "Manage Quizzes",
  reset_passwords: "Reset Passwords",
  manage_roles: "Manage Roles",
  manage_community: "Manage Community",
  // Section 9 — per-module permission keys
  manage_designers: "Designers · Certificate / Transcript builder",
  manage_admission_templates: "Admission Templates",
  manage_site_cms: "Public Site CMS",
  manage_leads: "Leads Management",
  manage_ai_tools: "AI Tools",
  manage_transcripts: "Transcripts Tool",
  manage_moderation: "Moderation Tools",
  // SBL Leadership POD
  access_pod: "POD · Full Access",
  access_pod_webinars: "POD · Webinars",
  access_pod_resources: "POD · Resources",
  access_pod_ai_templates: "POD · AI Templates",
  access_pod_roundtable: "POD · Roundtable",
  manage_pod: "POD · Manage (admin)",
  // Round 36 — granular finance & feedback permissions
  manage_feedback: "Manage Feedback Questions",
  manage_exchange_rates: "Manage Exchange Rates",
  manage_payment_approvals: "Approve Payments",
  manage_bank_account: "Manage Bank Account Details",
};

// Group permissions visually so super_admin can grant POD in full or in part.
const PERMISSION_GROUPS = [
  { label: "Core", keys: ["view_dashboard", "manage_programmes", "manage_courses", "manage_users", "create_users", "manage_payments", "view_analytics", "view_revenue"] },
  { label: "Operations", keys: ["manage_meetings", "manage_certificates", "manage_attendance", "manage_gallery", "manage_careers", "manage_forum", "manage_messages", "manage_quizzes", "reset_passwords", "manage_roles", "manage_community"] },
  { label: "Tools & Content", keys: ["manage_designers", "manage_admission_templates", "manage_site_cms", "manage_leads", "manage_ai_tools", "manage_transcripts", "manage_moderation", "manage_feedback"] },
  { label: "Finance", keys: ["manage_payment_approvals", "manage_exchange_rates", "manage_bank_account"] },
  { label: "SBL Leadership POD", keys: ["access_pod", "access_pod_webinars", "access_pod_resources", "access_pod_ai_templates", "access_pod_roundtable", "manage_pod"] },
];

export default function RoleManagementPage() {
  const [roles, setRoles] = useState([]);
  const [permissions, setPermissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [editingRole, setEditingRole] = useState(null);
  const [form, setForm] = useState({ name: "", label: "", permissions: [] });
  const [saving, setSaving] = useState(false);

  const loadRoles = () => {
    api.get("/roles").then(r => { setRoles(r.data); setLoading(false); }).catch(() => setLoading(false));
  };

  useEffect(() => {
    loadRoles();
    api.get("/permissions").then(r => setPermissions(r.data)).catch(() => {});
  }, []);

  const openCreateDialog = () => {
    setEditingRole(null);
    setForm({ name: "", label: "", permissions: [] });
    setShowDialog(true);
  };

  const openEditDialog = (role) => {
    setEditingRole(role);
    setForm({ name: role.name, label: role.label, permissions: [...role.permissions] });
    setShowDialog(true);
  };

  const togglePermission = (perm) => {
    setForm(prev => ({
      ...prev,
      permissions: prev.permissions.includes(perm)
        ? prev.permissions.filter(p => p !== perm)
        : [...prev.permissions, perm]
    }));
  };

  const handleSave = async () => {
    if (!form.name || !form.label) {
      toast.error("Name and label are required");
      return;
    }
    setSaving(true);
    try {
      if (editingRole) {
        await api.put(`/roles/${editingRole.name}`, form);
        toast.success("Role updated");
      } else {
        await api.post("/roles", form);
        toast.success("Role created");
      }
      setShowDialog(false);
      loadRoles();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to save role");
    }
    setSaving(false);
  };

  const handleDelete = async (roleName) => {
    if (!window.confirm(`Delete role "${roleName}"?`)) return;
    try {
      await api.delete(`/roles/${roleName}`);
      toast.success("Role deleted");
      loadRoles();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to delete role");
    }
  };

  return (
    <Layout>
      <div className="p-6 md:p-8 lg:p-12" data-testid="role-management-page">
        <div className="flex items-center justify-between mb-8 flex-wrap gap-4">
          <div>
            <p className="text-xs font-semibold uppercase tracking-widest text-slate-500 mb-1">Access Control</p>
            <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-slate-950" style={{ fontFamily: "'Outfit', sans-serif" }}>
              Role Management
            </h1>
          </div>
          <Button onClick={openCreateDialog} className="bg-blue-800 text-white hover:bg-blue-900 rounded-none gap-2" data-testid="create-role-btn">
            <Plus size={16} /> Create Role
          </Button>
        </div>

        {loading ? (
          <div className="space-y-3">{[1,2,3].map(i => <div key={i} className="h-16 bg-slate-100 animate-pulse" />)}</div>
        ) : (
          <Card className="border border-slate-200 rounded-none shadow-sm overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="text-xs uppercase tracking-wider">Role</TableHead>
                  <TableHead className="text-xs uppercase tracking-wider">Permissions</TableHead>
                  <TableHead className="text-xs uppercase tracking-wider">Type</TableHead>
                  <TableHead className="text-xs uppercase tracking-wider text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {roles.map((role, idx) => (
                  <TableRow key={role.name} data-testid={`role-row-${idx}`}>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <ShieldCheck size={18} className={role.is_builtin ? "text-blue-800" : "text-emerald-600"} />
                        <div>
                          <p className="text-sm font-medium text-slate-900">{role.label}</p>
                          <p className="text-xs text-slate-500 font-mono">{role.name}</p>
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex flex-wrap gap-1 max-w-md">
                        {role.permissions.slice(0, 4).map(p => (
                          <Badge key={p} variant="outline" className="rounded-none text-[10px]">{PERMISSION_LABELS[p] || p}</Badge>
                        ))}
                        {role.permissions.length > 4 && (
                          <Badge variant="secondary" className="rounded-none text-[10px]">+{role.permissions.length - 4} more</Badge>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge variant={role.is_builtin ? "default" : "secondary"} className="rounded-none text-xs">
                        {role.is_builtin ? "Built-in" : "Custom"}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right">
                      {role.name === "super_admin" ? (
                        <Lock size={16} className="text-slate-300 inline" />
                      ) : (
                        <div className="flex gap-1 justify-end">
                          <Button size="sm" variant="ghost" onClick={() => openEditDialog(role)} className="h-8 w-8 p-0" data-testid={`edit-role-${role.name}`}>
                            <Pencil size={14} />
                          </Button>
                          {!role.is_builtin && (
                            <Button size="sm" variant="ghost" onClick={() => handleDelete(role.name)} className="h-8 w-8 p-0 text-red-500 hover:text-red-700" data-testid={`delete-role-${role.name}`}>
                              <Trash size={14} />
                            </Button>
                          )}
                        </div>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Card>
        )}

        {/* Create/Edit Dialog */}
        <Dialog open={showDialog} onOpenChange={setShowDialog}>
          <DialogContent className="sm:max-w-lg rounded-none max-h-[80vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle style={{ fontFamily: "'Outfit', sans-serif" }}>
                {editingRole ? `Edit Role: ${editingRole.label}` : "Create New Role"}
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              {!editingRole && (
                <div>
                  <Label className="text-sm">Role Name (identifier)</Label>
                  <Input value={form.name} onChange={e => setForm({ ...form, name: e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, "_") })} className="mt-1 rounded-none font-mono" placeholder="e.g. hr_manager" data-testid="role-name-input" />
                </div>
              )}
              <div>
                <Label className="text-sm">Display Label</Label>
                <Input value={form.label} onChange={e => setForm({ ...form, label: e.target.value })} className="mt-1 rounded-none" placeholder="e.g. HR Manager" data-testid="role-label-input" />
              </div>
              <div>
                <Label className="text-sm mb-2 block">Permissions</Label>
                <div className="border border-slate-200 max-h-[28rem] overflow-y-auto">
                  {PERMISSION_GROUPS.map(group => {
                    const groupKeys = group.keys.filter(k => (permissions.length === 0 || permissions.includes(k)) && PERMISSION_LABELS[k]);
                    if (groupKeys.length === 0) return null;
                    const isPodGroup = group.label === "SBL Leadership POD";
                    return (
                      <div key={group.label} className={`border-b border-slate-200 last:border-b-0 p-3 ${isPodGroup ? "bg-amber-50/40" : ""}`} data-testid={`perm-group-${group.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
                        <div className="flex items-center justify-between mb-2">
                          <p className={`text-[10px] uppercase tracking-widest font-bold ${isPodGroup ? "text-amber-700" : "text-slate-500"}`} data-testid={`perm-group-label-${group.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}>{group.label}</p>
                          {isPodGroup && (
                            <button
                              type="button"
                              onClick={() => {
                                const allOn = groupKeys.every(k => form.permissions.includes(k));
                                const next = allOn
                                  ? form.permissions.filter(p => !groupKeys.includes(p))
                                  : Array.from(new Set([...form.permissions, ...groupKeys]));
                                setForm({ ...form, permissions: next });
                              }}
                              className="text-[10px] font-semibold uppercase tracking-widest text-amber-700 hover:text-amber-900 underline underline-offset-2"
                              data-testid="pod-group-toggle-all"
                            >
                              {groupKeys.every(k => form.permissions.includes(k)) ? "Revoke all POD" : "Grant all POD"}
                            </button>
                          )}
                        </div>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                          {groupKeys.map(perm => (
                            <label key={perm} className="flex items-center gap-2 text-sm cursor-pointer hover:bg-white p-1.5 transition-colors">
                              <Checkbox
                                checked={form.permissions.includes(perm)}
                                onCheckedChange={() => togglePermission(perm)}
                                data-testid={`perm-${perm}`}
                              />
                              <span className="text-slate-700">{PERMISSION_LABELS[perm] || perm}</span>
                            </label>
                          ))}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
              <div className="flex gap-2 justify-end pt-2">
                <Button variant="outline" onClick={() => setShowDialog(false)} className="rounded-none">Cancel</Button>
                <Button onClick={handleSave} disabled={saving} className="bg-blue-800 text-white hover:bg-blue-900 rounded-none" data-testid="save-role-btn">
                  {saving ? "Saving..." : editingRole ? "Update Role" : "Create Role"}
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
}
